import { Record, OrderedMap, Map } from 'immutable';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import { Vehicles } from 'models/vehicles';
import User from 'models/user';

import { GET_VEHICLES } from 'action-creators/account/get-vehicles';
import { GOT_VEHICLES } from 'action-creators/account/got-vehicles';
import { CHANGE_USER_INFO } from 'action-creators/account/change-user-info';
import { ADD_ERRORS } from 'action-creators/account/add-errors';
import { GOT_SESSION } from 'action-creators/account/got-session';
import { DISMISS_ERROR } from 'action-creators/account/dismiss-error';
import { INITIALIZE_SIGN_IN } from 'action-creators/account/initialize-sign-in';
import { INITIALIZE_SIGN_UP } from 'action-creators/account/initialize-sign-up';
import { INITIALIZE_PASSWORD_RECOVERY } from 'action-creators/account/initialize-password-recovery';
import { CLEANUP_SIGN_IN_STATE } from 'action-creators/account/cleanup-sign-in-state';
import { CLEANUP_SIGN_UP_STATE } from 'action-creators/account/cleanup-sign-up-state';
import { CLEANUP_PASSWORD_RECOVERY_STATE } from 'action-creators/account/cleanup-password-recovery-state';
import { GOT_VEHICLE } from 'action-creators/bookings/got-vehicle';
import { USER_UPDATED } from 'action-creators/account/user-updated';
import { ACTIVATE_ACCOUNT } from 'action-creators/account/activate-account';
import { SELECT_VEHICLE } from 'action-creators/vehicles/select-vehicle';
import { DELETED_VEHICLE } from 'action-creators/vehicles/deleted-vehicle';
import { SET_DEFAULT_VEHICLE } from 'action-creators/vehicles/set-default-vehicle';
import { INITIALIZE_NEW_VEHICLE } from 'action-creators/vehicles/initialize-new-vehicle';
import { SAVED_VEHICLE } from 'action-creators/vehicles/saved-vehicle';
import { UNSET_SUBMIT } from 'action-creators/payment-methods/unset-submit';
import { GOT_BOOKING_COUNTS } from 'action-creators/bookings/got-booking-counts';
import { CLEANUP_ENTERPRISE_INVITE } from 'action-creators/account/cleanup-enterprise-invite';

export class AccountState extends Record({
  user: new User(),
  vehicles: OrderedMap(),
  enterpriseInvite: null,
  errors: Map(),
  signInInitialized: false,
  signUpInitialized: false,
  passwordRecoveryInitialized: false,
  accountActivated: false,
  selectedVehicleId: null,
  defaultVehicleId: null,
  vehicle: null,
  submitting: false,
  signUpFields: Map(),
}) {
  constructor(props) {
    if (!props) {
      super();
      return;
    }

    const { user } = props;
    if (!user.token && props.token) {
      user.token = props.token;
    }

    const enterpriseInvite = props.enterpriseInvite && Map(props.enterpriseInvite);
    const {
      accountActivated = false,
    } = props || {};

    const defaultVehicle = props.vehicles ? props.vehicles.find(v => get(v, 'default', null)) : null;

    const signUpFields = props.account ? props.account.signUpFields : Map();

    super({
      user: new User(props.user),
      vehicles: Vehicles(props.vehicles),
      enterpriseInvite,
      accountActivated,
      defaultVehicleId: defaultVehicle && defaultVehicle.id,
      signUpFields,
    });
  }
}

export default function accountReducer(state = new AccountState(), action = null) {
  switch (action.type) {
    case GET_VEHICLES: {
      return state;
    }
    case GOT_VEHICLES: {
      let { vehicles } = action.payload;
      vehicles = state.vehicles.merge(vehicles);
      return state.merge({ vehicles });
    }
    case GOT_SESSION: {
      const { user } = action.payload;
      return state.merge({
        user,
      });
    }
    case CHANGE_USER_INFO:
      return state.merge({
        user: state.user.merge(action.payload),
      });
    case ADD_ERRORS: {
      const { errors } = action.payload;
      return state.merge({
        errors: state.errors.merge(errors),
      });
    }
    case DISMISS_ERROR: {
      const { error } = action.payload;
      const errors = state.errors.delete(error);
      return state.merge({
        errors,
      });
    }
    case INITIALIZE_SIGN_IN:
      return state.merge({
        signInInitialized: true,
      });
    case INITIALIZE_SIGN_UP:
      return state.merge({
        signUpInitialized: true,
      });
    case INITIALIZE_PASSWORD_RECOVERY:
      return state.merge({
        passwordRecoveryInitialized: true,
      });
    case CLEANUP_SIGN_IN_STATE:
      return state.merge({
        signInInitialized: false,
        errors: Map(),
      });
    case CLEANUP_SIGN_UP_STATE:
      return state.merge({
        signUpInitialized: false,
        errors: Map(),
      });
    case CLEANUP_PASSWORD_RECOVERY_STATE:
      return state.merge({
        passwordRecoveryInitialized: false,
        errors: Map(),
      });
    case CLEANUP_ENTERPRISE_INVITE:
      return state.merge({
        enterpriseInvite: null,
      });
    case GOT_VEHICLE: {
      const { vehicles } = state;
      const { vehicle } = action.payload;

      return state.merge({
        vehicles: vehicles.set(vehicle.id.toString(), vehicle),
      });
    }
    case USER_UPDATED: {
      const user = state.user.merge(action.payload);

      return state.merge({ user });
    }
    case ACTIVATE_ACCOUNT:
      return state.merge({
        accountActivated: true,
      });
    case SELECT_VEHICLE: {
      const { selectedVehicleId } = action.payload;
      const selectedVehicle = state.vehicles.get(selectedVehicleId);
      return state.merge({
        selectedVehicleId,
        vehicle: selectedVehicle,
      });
    }
    case DELETED_VEHICLE: {
      const { vehicleId } = action.payload;
      const vehicles = state.vehicles.delete(vehicleId);
      return state.merge({
        vehicles,
      });
    }
    case SET_DEFAULT_VEHICLE: {
      const { defaultVehicleId } = action.payload;

      return state.merge({
        defaultVehicleId,
      });
    }
    case INITIALIZE_NEW_VEHICLE: {
      const { vehicle } = action.payload;

      return state.merge({
        vehicle,
      });
    }
    case SAVED_VEHICLE: {
      const { vehicle } = action.payload;
      const vehicles = state.vehicles.set(`${vehicle.id}`, vehicle);
      return state.merge({ vehicles });
    }
    case UNSET_SUBMIT: {
      return state.merge({
        submitting: false,
      });
    }
    case GOT_BOOKING_COUNTS: {
      const { type, query, count } = action.payload;
      if (isEqual(type, ['bookings']) && isEqual(query, ['upcoming', 'active'])) {
        let { user } = state;
        user = user.set('bookingCount', count);
        return state.merge({ user });
      }

      return state;
    }
    default: {
      return state;
    }
  }
}
